import { constants } from './constants';
import { ROLE_CODES } from 'constants/role.constants';
import { UserProfile } from 'services/user/user.interface';
import {
  DEFAULT_PROFESSIONAL_MENU_MODULE_LABELS,
  LabelSettingRow,
  OrganizationsLabelType,
} from 'features/global-organizations-setting-state/global-organizations-setting.constant';
import _size from 'lodash-es/size';

export const START_DATE_TYPES = Object.freeze({
  DATE_OF_ASSIGNMENT: 1,
  OTHER_EVENT: 2,
  FOR_EACH_PHASE: 3,
});

export const EVENT_TYPES = Object.freeze({
  PROFESSIONAL_BIRTHDAY: 1,
  OTHER: 2,
});

export const ACCESS_LEVEL = Object.freeze({
  // Test registration certificate should have private access level
  PRIVATE: 1,
});

export enum ON_BEHALF_ACTIONS {
  PROCESSED = 1,
  COMPLETED,
  ACCEPTED,
  REJECTED,
}

export const ON_BEHALF_ACTION_NAMES = {
  [ON_BEHALF_ACTIONS.PROCESSED]: 'Processed',
  [ON_BEHALF_ACTIONS.COMPLETED]: 'Completed',
  [ON_BEHALF_ACTIONS.ACCEPTED]: 'Accepted',
  [ON_BEHALF_ACTIONS.REJECTED]: 'Rejected',
};

export const ON_BEHALF_ROLE_LABEL = {
  [ROLE_CODES.ORGANIZATION_ADMIN]: 'A',
  [ROLE_CODES.IMPOSER]: 'IM',
};

export const TEST_REGISTRATION_COMPLIANCE_OPTIONS = {
  END: {
    id: 0,
    name: 'End',
  },
  REGISTRATION_FORM: {
    id: 1,
    name: constants.TEST_REGISTRATION_PHASES.REGISTRATION_FORM,
  },
  TEST: {
    id: 2,
    name: constants.TEST_REGISTRATION_PHASES.TEST,
  },
};

const CERTIFICATION = {
  START_DATE_TYPES,
  EVENT_TYPES,
  ACCESS_LEVEL,
  ON_BEHALF_ACTIONS,
  TEST_REGISTRATION_COMPLIANCE_OPTIONS,
};

export default CERTIFICATION;

export enum WORKFLOW_SUB_TYPES {
  DEFAULT = 0,
  LICENSE = 1,
  VERIFICATION = 2,
  RENEWAL = 3,
  SPECIALTY = 4,
  GENERAL = 5,
  TESTING = 6,
  MISC = 7,
  ONBOARDING = 8,
}

export const DISABLED_AFTER_PUBLISH_FIELDS = {
  [WORKFLOW_SUB_TYPES.LICENSE]:
    'Credential type, Acronym, Master Acronym, Credential name, Admin only credential parameter',
  [WORKFLOW_SUB_TYPES.SPECIALTY]:
    'Credential type, Acronym, Master Acronym, Credential name, Admin only credential parameter, Credential to link parameter',
  [WORKFLOW_SUB_TYPES.RENEWAL]: 'Credential type, Admin only credential parameter, Credential to link parameter',
  [WORKFLOW_SUB_TYPES.VERIFICATION]: 'Credential type, Admin only credential parameter, Credential to link parameter',
  [WORKFLOW_SUB_TYPES.MISC]: 'Credential type, Admin only credential parameter',
  [WORKFLOW_SUB_TYPES.ONBOARDING]: 'Credential type, Admin only credential parameter',
  [WORKFLOW_SUB_TYPES.GENERAL]: 'Acronym, Master Acronym, Credential name',
};

export enum CREDENTIAL_CONFIGURATION_WARNINGS {
  ACRONYM_DIFFERENT_NAME = 'ACRONYM_DIFFERENT_NAME',
  ACRONYM_CREDENTIAL_NAME = 'ACRONYM_CREDENTIAL_NAME',
  ACRONYM_EQUAL_MASTER_ACRONYM = 'ACRONYM_EQUAL_MASTER_ACRONYM',
  MASTER_ACRONYM_DATA_NOT_ACTIVE = 'MASTER_ACRONYM_DATA_NOT_ACTIVE',
  ACRONYM_DATA_NOT_ACTIVE = 'ACRONYM_DATA_NOT_ACTIVE',
  WORKFLOW_NAME = 'WORKFLOW_NAME',
  CREDENTIAL_NAME_ACRONYM = 'CREDENTIAL_NAME_ACRONYM',
}

export const INFO_MESSAGES = {
  PHASE_END_DATE: 'When is the phase supposed to end?',
  PHASE_END: 'How is the phase supposed to end?',
  ACTION_TAKEN: 'What happens when all requirements are met?',
  REGISTRY_UPDATES_TIMING: 'When should the registry updates be made (and certificate generated, if applicable)?',
};

export const COMPLIANCE_CREDENTIAL_CONFIGURATION_ERROR_MESSAGES_TEMPLATE = {
  [CREDENTIAL_CONFIGURATION_WARNINGS.WORKFLOW_NAME]:
    'The Workflow name is already in use by your organization. Please enter a different Workflow name.',
  [CREDENTIAL_CONFIGURATION_WARNINGS.ACRONYM_EQUAL_MASTER_ACRONYM]:
    'The acronym cannot be the same as the master acronym for the Specialty workflow you have selected. Please enter a different acronym or select a different workflow type.',
  [CREDENTIAL_CONFIGURATION_WARNINGS.ACRONYM_CREDENTIAL_NAME]:
    'The acronym you have selected for this workflow is already associated with a different Credential Name than the one you have selected. Please update the Credential Name to {name[0]} or select another Acronym.',
  [CREDENTIAL_CONFIGURATION_WARNINGS.CREDENTIAL_NAME_ACRONYM]:
    'The Credential Name you have selected for this workflow is already associated with a different Acronym than the one you have selected. Please update the Acronym to {name[0]} or select another Credential Name.',
  [CREDENTIAL_CONFIGURATION_WARNINGS.ACRONYM_DIFFERENT_NAME]:
    'The acronym you have selected for this workflow is already associated with a different master acronym and credential name {name[0]} than the one you have selected. Please enter a different acronym for this workflow.',
  [CREDENTIAL_CONFIGURATION_WARNINGS.MASTER_ACRONYM_DATA_NOT_ACTIVE]:
    'The Master Acronym associated with this Specialty workflow no longer has an active [License/General] workflow associated with it. Please select a different master acronym to link to this workflow to proceed.',
  [CREDENTIAL_CONFIGURATION_WARNINGS.ACRONYM_DATA_NOT_ACTIVE]:
    'The Acronym associated with this workflow no longer has an active [License/General/Specialty] workflow associated with it. Please select a different acronym to link to this workflow to proceed.',
};

export const publishMappingErrorModalConfig = {
  title: 'Phases are not mapped',
  body: 'Versions contain phases that are not mapped. Please map phases before publishing credential.',
  rejectButtonText: 'Close',
  successButtonText: 'Go to versions',
};

export function getMenuTitles(
  currentProfile: UserProfile,
): {
  credentialTitle: string;
  testingTitle: string;
} {
  let credentialTitle = '';
  let defaultCredentialTitle = '';
  let testingTitle = '';

  if (_size(currentProfile.labels)) {
    currentProfile.labels.forEach((label: LabelSettingRow) => {
      switch (label.labelTypeId) {
        case OrganizationsLabelType.MODULE_CREDENTIALING_PROF:
          credentialTitle = label.organizationLabel || credentialTitle;
          defaultCredentialTitle = label.defaultLabel;
          break;
        case OrganizationsLabelType.MODULE_COMPLIANCE_PROF:
          credentialTitle = credentialTitle || label.organizationLabel;
          defaultCredentialTitle = label.defaultLabel;
          break;
        case OrganizationsLabelType.MODULE_TESTING_PROF:
          testingTitle = label.organizationLabel;
          break;
      }
    });
  }

  credentialTitle =
    credentialTitle ||
    defaultCredentialTitle ||
    DEFAULT_PROFESSIONAL_MENU_MODULE_LABELS[OrganizationsLabelType.MODULE_CREDENTIALING_PROF];
  testingTitle = testingTitle || DEFAULT_PROFESSIONAL_MENU_MODULE_LABELS[OrganizationsLabelType.MODULE_TESTING_PROF];

  return {
    credentialTitle,
    testingTitle,
  };
}
